import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";

const links = [
  { label: "About", href: "#about" },
  { label: "Commodities", href: "#commodities" },
  { label: "Trade Flow", href: "#trade-flow" },
  { label: "Services", href: "#services" },
  { label: "Our Approach", href: "#why" },
  { label: "Contact", href: "#contact" },
];

// The uploaded VG monogram is square with no text — display in full
const LogoMark = ({ size = 56 }) => (
  <div
    className="relative overflow-hidden rounded-full shrink-0 ring-1 ring-vynex-silver/30 bg-white"
    style={{ width: size, height: size }}
    data-testid="vynex-logo"
  >
    <img
      src="/vynex-logo.png"
      alt="Vynex Global"
      className="w-full h-full object-cover"
      style={{ transform: "scale(1.05)" }}
    />
  </div>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-vynex-navy/95 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-24">
        <a
          href="#top"
          className="flex items-center gap-4 group"
          data-testid="logo-link"
        >
          <LogoMark size={56} />
          <div className="flex flex-col justify-center leading-tight">
            <div className="text-white font-display text-xl tracking-tight">
              Vynex Global
            </div>
            <div className="text-vynex-silver/60 eyebrow text-[9px] mt-1">
              Pte Ltd
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-vynex-silver/80 hover:text-white text-sm tracking-wide transition-colors relative group"
              data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-vynex-gold transition-all duration-300 group-hover:w-full"></span>
            </a>
          ))}
        </nav>

        <a
          href="#contact"
          className="hidden lg:inline-flex items-center px-5 py-2.5 text-[10px] tracking-[0.25em] uppercase border border-vynex-gold/50 text-vynex-gold hover:bg-vynex-gold hover:text-vynex-navy transition-all duration-300"
          data-testid="navbar-cta-contact"
        >
          Enquire
        </a>

        <button
          aria-label="menu"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden text-white"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={26} /> : <List size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden bg-vynex-navy-deep border-t border-white/10 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-vynex-silver/90 text-base tracking-wide hover:text-vynex-gold"
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
